import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import AppLayout from '../components/AppLayout';
import { useAuth } from '../contexts/AuthContext';
import { 
  AlertTriangle, Trash2, RefreshCw, Database, FileText, 
  Building2, Activity, Shield, CheckCircle, XCircle,
  AlertCircle, Users, DollarSign, Lock, Key, ArrowRight,
  UserX, Search, HardDrive, Download, Cloud, Clock, FolderArchive,
  GitMerge
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const AdminSandbox = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [sandboxStatus, setSandboxStatus] = useState(null);
  const [clearing, setClearing] = useState({});
  const [message, setMessage] = useState(null);
  
  // PIN verification state
  const [pinStatus, setPinStatus] = useState(null);
  const [showPinModal, setShowPinModal] = useState(false);
  const [pin, setPin] = useState('');
  const [verifyingPin, setVerifyingPin] = useState(false);
  
  // 3-step confirmation state
  const [confirmationFlow, setConfirmationFlow] = useState({
    active: false,
    step: 1,
    action: null,
    type: null,
    typedConfirm: '',
    finalConfirm: ''
  });
  
  // Empty contacts cleanup state
  const [emptyContactsPreview, setEmptyContactsPreview] = useState(null);
  const [showEmptyContactsModal, setShowEmptyContactsModal] = useState(false);
  const [cleaningContacts, setCleaningContacts] = useState(false);
  const [previewingContacts, setPreviewingContacts] = useState(false);
  
  // Backup state
  const [backups, setBackups] = useState({ local_backups: [], google_drive_backups: [] });
  const [creatingBackup, setCreatingBackup] = useState(false);
  const [loadingBackups, setLoadingBackups] = useState(false);
  
  // Migration state
  const [runningMigration, setRunningMigration] = useState(false);
  const [migrationResult, setMigrationResult] = useState(null);

  useEffect(() => {
    fetchStatus();
    fetchPinStatus();
    fetchBackups();
  }, []);

  const fetchStatus = async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/sandbox/status`, {
        credentials: 'include'
      });
      if (response.ok) {
        const data = await response.json();
        setSandboxStatus(data);
      } else {
        setSandboxStatus({ sandbox_enabled: false });
      }
    } catch (error) {
      console.error('Failed to fetch sandbox status:', error);
      setSandboxStatus({ sandbox_enabled: false });
    } finally {
      setLoading(false);
    }
  };

  const fetchPinStatus = async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/admin-pin/status`, {
        credentials: 'include'
      });
      if (response.ok) {
        const data = await response.json();
        setPinStatus(data);
      }
    } catch (error) {
      console.error('Failed to fetch PIN status:', error);
    }
  };

  const verifyPin = async () => {
    if (!pin) return;
    
    setVerifyingPin(true);
    setMessage(null);
    
    try {
      const response = await fetch(`${BACKEND_URL}/api/admin-pin/verify`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ pin })
      });
      
      if (response.ok) {
        setShowPinModal(false);
        setPin('');
        await fetchStatus();
        await fetchPinStatus();
        setMessage({ type: 'success', text: 'PIN verified successfully. You can now perform sandbox operations.' });
      } else {
        const error = await response.json();
        setMessage({ type: 'error', text: error.detail || 'PIN verification failed' });
      }
    } catch (error) {
      setMessage({ type: 'error', text: 'Failed to verify PIN' });
    } finally {
      setVerifyingPin(false);
    }
  };

  const startConfirmationFlow = (type, action) => {
    setConfirmationFlow({
      active: true,
      step: 1,
      action,
      type,
      typedConfirm: '',
      finalConfirm: ''
    });
    setMessage(null);
  };

  const cancelConfirmationFlow = () => {
    setConfirmationFlow({
      active: false,
      step: 1,
      action: null,
      type: null,
      typedConfirm: '',
      finalConfirm: ''
    });
  };

  const nextStep = () => {
    const { step, typedConfirm, type } = confirmationFlow;
    
    if (step === 2) {
      // Validate typed confirmation
      const requiredText = type === 'reset-all' ? 'DELETE ALL DATA' : `DELETE ${type.toUpperCase()}`;
      if (typedConfirm !== requiredText) {
        setMessage({ type: 'error', text: `Please type "${requiredText}" exactly to continue` });
        return;
      }
    }
    
    setConfirmationFlow(prev => ({ ...prev, step: prev.step + 1 }));
    setMessage(null);
  };

  const executeAction = async () => {
    const { type, finalConfirm } = confirmationFlow;
    
    if (finalConfirm !== 'CONFIRM') {
      setMessage({ type: 'error', text: 'Please type "CONFIRM" to execute the action' });
      return;
    }
    
    setClearing({ ...clearing, [type]: true });
    setMessage(null);
    
    try {
      const endpoint = type === 'reset-all' ? 'reset-all' : type;
      const options = {
        method: 'DELETE',
        credentials: 'include'
      };
      
      if (type === 'reset-all') {
        options.headers = { 'Content-Type': 'application/json' };
        options.body = JSON.stringify({ confirmation: 'RESET' });
      }
      
      const response = await fetch(`${BACKEND_URL}/api/sandbox/${endpoint}`, options);
      
      if (response.ok) {
        const data = await response.json();
        const deletedCount = data.deleted_count || (data.deleted ? Object.values(data.deleted).reduce((a, b) => a + b, 0) : 0);
        setMessage({ type: 'success', text: `${data.message} (${deletedCount} items deleted)` });
        fetchStatus();
      } else {
        const error = await response.json();
        setMessage({ type: 'error', text: error.detail || 'Failed to clear data' });
      }
    } catch (error) {
      setMessage({ type: 'error', text: 'Failed to clear data' });
    } finally {
      setClearing({ ...clearing, [type]: false });
      cancelConfirmationFlow();
    }
  };

  // Empty Contacts Cleanup Functions
  const previewEmptyContacts = async () => {
    setPreviewingContacts(true);
    setMessage(null);
    
    try {
      const response = await fetch(`${BACKEND_URL}/api/contacts/cleanup/preview`, {
        credentials: 'include'
      });
      
      if (response.ok) {
        const data = await response.json();
        setEmptyContactsPreview(data);
        setShowEmptyContactsModal(true);
      } else {
        const error = await response.json();
        setMessage({ type: 'error', text: error.detail || 'Failed to preview empty contacts' });
      }
    } catch (error) {
      setMessage({ type: 'error', text: 'Failed to preview empty contacts' });
    } finally {
      setPreviewingContacts(false);
    }
  };

  const executeEmptyContactsCleanup = async () => {
    setCleaningContacts(true);
    setMessage(null);
    
    try {
      const response = await fetch(`${BACKEND_URL}/api/contacts/cleanup`, {
        method: 'POST',
        credentials: 'include'
      });
      
      if (response.ok) {
        const data = await response.json();
        setMessage({ type: 'success', text: data.message });
        setShowEmptyContactsModal(false);
        setEmptyContactsPreview(null);
      } else {
        const error = await response.json();
        setMessage({ type: 'error', text: error.detail || 'Failed to clean up empty contacts' });
      }
    } catch (error) {
      setMessage({ type: 'error', text: 'Failed to clean up empty contacts' });
    } finally {
      setCleaningContacts(false);
    }
  };

  // Backup Functions
  const fetchBackups = async () => {
    setLoadingBackups(true);
    try {
      const response = await fetch(`${BACKEND_URL}/api/admin/backups`, {
        credentials: 'include'
      });
      
      if (response.ok) {
        const data = await response.json();
        setBackups(data);
      }
    } catch (error) {
      console.error('Failed to fetch backups:', error);
    } finally {
      setLoadingBackups(false);
    }
  };

  const createBackup = async () => {
    setCreatingBackup(true);
    setMessage(null);
    
    try {
      const response = await fetch(`${BACKEND_URL}/api/admin/backup`, {
        method: 'POST',
        credentials: 'include'
      });
      
      if (response.ok) {
        const data = await response.json();
        setMessage({ 
          type: 'success', 
          text: `Backup created: ${data.filename} (${data.size_mb} MB)${data.google_drive_uploaded ? ' - Uploaded to Google Drive' : ''}` 
        });
        fetchBackups();
      } else {
        const error = await response.json();
        setMessage({ type: 'error', text: error.detail || 'Failed to create backup' });
      }
    } catch (error) {
      setMessage({ type: 'error', text: 'Failed to create backup' });
    } finally {
      setCreatingBackup(false);
    }
  };

  const downloadBackup = (filename) => {
    window.open(`${BACKEND_URL}/api/admin/backups/${filename}/download`, '_blank');
  };

  const deleteBackup = async (filename) => {
    if (!window.confirm(`Are you sure you want to delete ${filename}?`)) return;
    
    try {
      const response = await fetch(`${BACKEND_URL}/api/admin/backups/${filename}`, {
        method: 'DELETE',
        credentials: 'include'
      });
      
      if (response.ok) {
        setMessage({ type: 'success', text: `Deleted backup: ${filename}` });
        fetchBackups();
      } else {
        const error = await response.json();
        setMessage({ type: 'error', text: error.detail || 'Failed to delete backup' });
      }
    } catch (error) {
      setMessage({ type: 'error', text: 'Failed to delete backup' });
    }
  };

  const formatDate = (dateString) => {
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('en-GB', { 
        day: '2-digit', 
        month: 'short', 
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch {
      return dateString;
    }
  };

  // Migration function for proposal account managers
  const runProposalMigration = async () => {
    if (!window.confirm('This will update all existing proposals to use account_manager_id based on the card\'s relationship owner. Continue?')) {
      return;
    }
    
    setRunningMigration(true);
    setMigrationResult(null);
    setMessage(null);
    
    try {
      const response = await fetch(`${BACKEND_URL}/api/admin/migrate-proposal-account-managers`, {
        method: 'POST',
        credentials: 'include'
      });
      
      if (response.ok) {
        const data = await response.json();
        setMigrationResult(data);
        setMessage({ 
          type: 'success', 
          text: `Migration complete: ${data.updated} proposals updated, ${data.skipped} skipped` 
        });
      } else {
        const error = await response.json();
        setMessage({ type: 'error', text: error.detail || 'Migration failed' });
      }
    } catch (error) {
      setMessage({ type: 'error', text: 'Migration failed: ' + error.message });
    } finally {
      setRunningMigration(false);
    }
  };

  // Check if user is admin
  if (user && user.role !== 'admin') {
    return (
      <AppLayout>
        <div className="min-h-screen flex items-center justify-center">
          <div className="text-center">
            <Shield className="w-16 h-16 text-red-500 mx-auto mb-4" />
            <h1 className="text-2xl font-bold text-white mb-2">Access Denied</h1>
            <p className="text-gray-400">Admin access required for this page.</p>
          </div>
        </div>
      </AppLayout>
    );
  }

  // Sandbox not enabled at all
  if (sandboxStatus && !sandboxStatus.sandbox_enabled) {
    return (
      <AppLayout>
        <div className="min-h-screen flex items-center justify-center">
          <div className="text-center max-w-md">
            <XCircle className="w-16 h-16 text-red-500 mx-auto mb-4" />
            <h1 className="text-2xl font-bold text-white mb-2">Sandbox Disabled</h1>
            <p className="text-gray-400 mb-4">
              Sandbox mode is not enabled in this environment.
            </p>
            <p className="text-gray-500 text-sm">
              Set SANDBOX_MODE=true in your environment to enable sandbox features.
            </p>
          </div>
        </div>
      </AppLayout>
    );
  }

  if (loading) {
    return (
      <AppLayout>
        <div className="min-h-screen flex items-center justify-center">
          <div className="text-[#A2FF00]">Loading...</div>
        </div>
      </AppLayout>
    );
  }

  const counts = sandboxStatus?.counts || {};
  const isProduction = sandboxStatus?.is_production;
  const pinRequired = sandboxStatus?.pin_required;
  const pinVerified = sandboxStatus?.pin_verified;
  const canPerformActions = !pinRequired || pinVerified;

  const getConfirmationText = (type) => {
    return type === 'reset-all' ? 'DELETE ALL DATA' : `DELETE ${type.toUpperCase()}`;
  };

  return (
    <AppLayout>
      <div className="p-6 max-w-4xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center gap-3 mb-2">
            <Database className="w-8 h-8 text-[#A2FF00]" />
            <h1 className="text-2xl font-bold text-white" style={{ fontFamily: 'Unbounded, sans-serif' }}>
              Sandbox Environment
            </h1>
          </div>
          <p className="text-gray-400">
            Manage test data in your environment. These actions cannot be undone.
          </p>
        </div>

        {/* Production Warning Banner */}
        {isProduction && (
          <div className="mb-6 p-4 bg-orange-500/20 border border-orange-500/50 rounded-sm">
            <div className="flex items-start gap-3">
              <AlertTriangle className="w-6 h-6 text-orange-400 flex-shrink-0" />
              <div>
                <h3 className="text-orange-400 font-bold">Production Environment Detected</h3>
                <p className="text-gray-300 text-sm mt-1">
                  You are in a production environment. Sandbox operations require PIN verification and 3-step confirmation to prevent accidental data loss.
                </p>
              </div>
            </div>
          </div>
        )}

        {/* PIN Verification Status */}
        {pinRequired && (
          <div className={`mb-6 p-4 rounded-sm border flex items-center justify-between ${
            pinVerified 
              ? 'bg-green-500/10 border-green-500/30' 
              : 'bg-yellow-500/10 border-yellow-500/30'
          }`}>
            <div className="flex items-center gap-3">
              {pinVerified ? (
                <>
                  <Lock className="w-5 h-5 text-green-400" />
                  <span className="text-green-400 font-medium">PIN Verified</span>
                  <span className="text-gray-400 text-sm">- Sandbox operations enabled</span>
                </>
              ) : (
                <>
                  <Key className="w-5 h-5 text-yellow-400" />
                  <span className="text-yellow-400 font-medium">PIN Verification Required</span>
                  <span className="text-gray-400 text-sm">- Enter your admin PIN to enable sandbox operations</span>
                </>
              )}
            </div>
            {!pinVerified && (
              <button
                onClick={() => setShowPinModal(true)}
                className="flex items-center gap-2 px-4 py-2 bg-yellow-500 hover:bg-yellow-600 text-black font-bold rounded-sm transition-colors"
              >
                <Key className="w-4 h-4" />
                Verify PIN
              </button>
            )}
          </div>
        )}

        {/* Status Banner */}
        <div className={`mb-6 p-4 rounded-sm border flex items-center gap-3 ${
          sandboxStatus?.sandbox_enabled 
            ? 'bg-green-500/10 border-green-500/30' 
            : 'bg-red-500/10 border-red-500/30'
        }`}>
          {sandboxStatus?.sandbox_enabled ? (
            <>
              <CheckCircle className="w-5 h-5 text-green-400" />
              <span className="text-green-400 font-medium">Sandbox Mode Active</span>
              <span className="text-gray-400 text-sm ml-2">
                {isProduction ? '- 3-step confirmation required for all operations' : '- Data clearing is enabled'}
              </span>
            </>
          ) : (
            <>
              <XCircle className="w-5 h-5 text-red-400" />
              <span className="text-red-400 font-medium">Sandbox Mode Disabled</span>
            </>
          )}
        </div>

        {/* Message */}
        {message && (
          <div className={`mb-6 p-4 rounded-sm border flex items-center gap-3 ${
            message.type === 'success' 
              ? 'bg-green-500/10 border-green-500/30' 
              : 'bg-red-500/10 border-red-500/30'
          }`}>
            {message.type === 'success' ? (
              <CheckCircle className="w-5 h-5 text-green-400" />
            ) : (
              <XCircle className="w-5 h-5 text-red-400" />
            )}
            <span className={message.type === 'success' ? 'text-green-400' : 'text-red-400'}>
              {message.text}
            </span>
          </div>
        )}

        {/* Warning */}
        <div className="mb-6 p-4 bg-yellow-500/10 border border-yellow-500/30 rounded-sm flex items-start gap-3">
          <AlertTriangle className="w-5 h-5 text-yellow-400 flex-shrink-0 mt-0.5" />
          <div>
            <p className="text-yellow-400 font-medium">Warning: Destructive Actions</p>
            <p className="text-gray-400 text-sm mt-1">
              Clearing data is permanent and cannot be undone. Products, Templates, and Library Sections are preserved.
              {isProduction && ' Each action requires 3-step confirmation with PIN verification.'}
            </p>
          </div>
        </div>

        {/* Data Cards */}
        <div className="space-y-4 mb-8">
          {/* Proposals */}
          <DataCard
            icon={FileText}
            iconBg="bg-blue-500/20"
            iconColor="text-blue-400"
            title="Proposals"
            count={counts.proposals || 0}
            description="Includes: proposals, activities, notifications"
            onClear={() => startConfirmationFlow('proposals', 'Clear Proposals')}
            clearing={clearing.proposals}
            disabled={!canPerformActions}
          />

          {/* Cards */}
          <DataCard
            icon={Building2}
            iconBg="bg-purple-500/20"
            iconColor="text-purple-400"
            title="Cards / Companies"
            count={counts.cards || 0}
            description="Includes: cards, contacts, card activities, notes"
            onClear={() => startConfirmationFlow('cards', 'Clear Cards')}
            clearing={clearing.cards}
            disabled={!canPerformActions}
          />

          {/* Activities */}
          <DataCard
            icon={Activity}
            iconBg="bg-orange-500/20"
            iconColor="text-orange-400"
            title="Activity Logs"
            count={counts.activities || 0}
            description="Includes: proposal activities, card activities, notifications"
            onClear={() => startConfirmationFlow('activities', 'Clear Activities')}
            clearing={clearing.activities}
            disabled={!canPerformActions}
          />

          {/* Users */}
          <DataCard
            icon={Users}
            iconBg="bg-cyan-500/20"
            iconColor="text-cyan-400"
            title="Test Users"
            count={counts.users || 0}
            description="Deletes all users except the current admin. Sessions are also cleared."
            onClear={() => startConfirmationFlow('users', 'Clear Users')}
            clearing={clearing.users}
            disabled={!canPerformActions}
          />

          {/* Financial Data */}
          <DataCard
            icon={DollarSign}
            iconBg="bg-yellow-500/20"
            iconColor="text-yellow-400"
            title="Financial / Payment Data"
            count={`${counts.contracts || 0} contracts, ${counts.payment_transactions || 0} transactions`}
            description="Includes: contracts, payment transactions, payment records. API keys are preserved."
            onClear={() => startConfirmationFlow('financial', 'Clear Financial Data')}
            clearing={clearing.financial}
            disabled={!canPerformActions}
            borderColor="border-yellow-500/30"
          />

          {/* Empty Field Clean - Data Maintenance Tool */}
          <div className="bg-[#111] border border-cyan-500/30 rounded-sm p-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-cyan-500/20 rounded-sm flex items-center justify-center">
                  <UserX className="w-5 h-5 text-cyan-400" />
                </div>
                <div>
                  <h3 className="text-white font-medium">Empty Field Clean</h3>
                  <p className="text-gray-500 text-sm">Remove contacts with no name or email</p>
                </div>
              </div>
              <button
                onClick={previewEmptyContacts}
                disabled={previewingContacts || !canPerformActions}
                className="flex items-center gap-2 px-4 py-2 bg-cyan-500/20 hover:bg-cyan-500/30 text-cyan-400 rounded-sm transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                data-testid="empty-field-clean-btn"
              >
                {previewingContacts ? (
                  <RefreshCw className="w-4 h-4 animate-spin" />
                ) : (
                  <Search className="w-4 h-4" />
                )}
                Preview & Clean
              </button>
            </div>
            <p className="text-gray-500 text-xs mt-2 ml-13">
              Scans all cards for contacts missing required fields (name, email). Shows preview before cleaning.
            </p>
          </div>

          {/* Proposal Account Manager Migration */}
          <div className="bg-[#111] border border-purple-500/30 rounded-sm p-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-purple-500/20 rounded-sm flex items-center justify-center">
                  <GitMerge className="w-5 h-5 text-purple-400" />
                </div>
                <div>
                  <h3 className="text-white font-medium">Proposal Account Manager Migration</h3>
                  <p className="text-gray-500 text-sm">Backfill account_manager_id on existing proposals</p>
                </div>
              </div>
              <button
                onClick={runProposalMigration}
                disabled={runningMigration || !canPerformActions}
                className="flex items-center gap-2 px-4 py-2 bg-purple-500/20 hover:bg-purple-500/30 text-purple-400 rounded-sm transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                data-testid="run-migration-btn"
              >
                {runningMigration ? (
                  <>
                    <RefreshCw className="w-4 h-4 animate-spin" />
                    Running...
                  </>
                ) : (
                  <>
                    <GitMerge className="w-4 h-4" />
                    Run Migration
                  </>
                )}
              </button>
            </div>
            <p className="text-gray-500 text-xs mt-2 ml-13">
              Updates proposals to attribute them to the card's account manager instead of the creator. Required for accurate leaderboard/dashboard metrics.
            </p>
            {migrationResult && (
              <div className="mt-3 p-3 bg-purple-500/10 border border-purple-500/20 rounded-sm">
                <p className="text-purple-300 text-sm">
                  <span className="font-medium">Last Migration Result:</span> {migrationResult.updated} updated, {migrationResult.skipped} skipped
                  {migrationResult.errors?.length > 0 && (
                    <span className="text-red-400"> ({migrationResult.errors.length} errors)</span>
                  )}
                </p>
              </div>
            )}
          </div>

          {/* Preserved Data Info */}
          <div className="bg-[#111] border border-white/10 rounded-sm p-4 opacity-60">
            <div className="flex items-center gap-3 mb-3">
              <Shield className="w-5 h-5 text-green-400" />
              <h3 className="text-white font-medium">Preserved Data (Not Cleared)</h3>
            </div>
            <div className="grid grid-cols-3 gap-4 text-sm">
              <div className="flex items-center gap-2">
                <CheckCircle className="w-4 h-4 text-green-400" />
                <span className="text-gray-400">Products ({counts.products || 0})</span>
              </div>
              <div className="flex items-center gap-2">
                <CheckCircle className="w-4 h-4 text-green-400" />
                <span className="text-gray-400">Templates ({counts.templates || 0})</span>
              </div>
              <div className="flex items-center gap-2">
                <CheckCircle className="w-4 h-4 text-green-400" />
                <span className="text-gray-400">Library Sections ({counts.library_sections || 0})</span>
              </div>
              <div className="flex items-center gap-2">
                <CheckCircle className="w-4 h-4 text-green-400" />
                <span className="text-gray-400">Board Structure</span>
              </div>
            </div>
          </div>
        </div>

        {/* Backup Section */}
        <div className="mt-8 mb-8">
          <div className="flex items-center gap-3 mb-4">
            <HardDrive className="w-6 h-6 text-[#A2FF00]" />
            <h2 className="text-xl font-bold text-white" style={{ fontFamily: 'Unbounded, sans-serif' }}>
              Backup Management
            </h2>
          </div>
          
          <div className="bg-[#111] border border-[#A2FF00]/30 rounded-sm p-6 mb-4">
            <div className="flex items-center justify-between mb-4">
              <div>
                <h3 className="text-white font-medium">Create Backup</h3>
                <p className="text-gray-500 text-sm">
                  Creates a full backup of database and uploaded files. Automatically uploads to Google Drive.
                </p>
              </div>
              <button
                onClick={createBackup}
                disabled={creatingBackup || !canPerformActions}
                className="flex items-center gap-2 px-6 py-3 bg-[#A2FF00] hover:bg-[#8FE600] text-black font-bold rounded-sm transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                data-testid="create-backup-btn"
              >
                {creatingBackup ? (
                  <>
                    <RefreshCw className="w-4 h-4 animate-spin" />
                    Creating Backup...
                  </>
                ) : (
                  <>
                    <FolderArchive className="w-4 h-4" />
                    Create Backup Now
                  </>
                )}
              </button>
            </div>
            
            <div className="flex items-center gap-4 text-sm text-gray-500">
              <div className="flex items-center gap-2">
                <Clock className="w-4 h-4" />
                <span>Auto-backup: Daily at 2:00 AM UTC</span>
              </div>
              <div className="flex items-center gap-2">
                <Cloud className="w-4 h-4" />
                <span>Storage: Local + Google Drive</span>
              </div>
              <div className="flex items-center gap-2">
                <Shield className="w-4 h-4" />
                <span>Retention: 7 days</span>
              </div>
            </div>
          </div>

          {/* Local Backups List */}
          <div className="bg-[#111] border border-white/10 rounded-sm p-4 mb-4">
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-white font-medium flex items-center gap-2">
                <HardDrive className="w-4 h-4 text-blue-400" />
                Local Backups
              </h3>
              <button
                onClick={fetchBackups}
                disabled={loadingBackups}
                className="text-gray-400 hover:text-white"
              >
                <RefreshCw className={`w-4 h-4 ${loadingBackups ? 'animate-spin' : ''}`} />
              </button>
            </div>
            
            {backups.local_backups.length === 0 ? (
              <p className="text-gray-500 text-sm">No local backups found</p>
            ) : (
              <div className="space-y-2">
                {backups.local_backups.map((backup, idx) => (
                  <div key={idx} className="flex items-center justify-between bg-[#0A0A0A] border border-white/5 rounded-sm p-3">
                    <div className="flex items-center gap-3">
                      <FileText className="w-4 h-4 text-gray-500" />
                      <div>
                        <p className="text-white text-sm font-medium">{backup.filename}</p>
                        <p className="text-gray-500 text-xs">
                          {formatDate(backup.created_at)} • {backup.size_mb} MB • {backup.total_documents} documents • {backup.total_files} files
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <button
                        onClick={() => downloadBackup(backup.filename)}
                        className="p-2 text-blue-400 hover:bg-blue-500/20 rounded-sm transition-colors"
                        title="Download"
                      >
                        <Download className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => deleteBackup(backup.filename)}
                        className="p-2 text-red-400 hover:bg-red-500/20 rounded-sm transition-colors"
                        title="Delete"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Google Drive Backups List */}
          <div className="bg-[#111] border border-white/10 rounded-sm p-4">
            <h3 className="text-white font-medium flex items-center gap-2 mb-3">
              <Cloud className="w-4 h-4 text-green-400" />
              Google Drive Backups
            </h3>
            
            {backups.google_drive_backups.length === 0 ? (
              <p className="text-gray-500 text-sm">No Google Drive backups found. Make sure Drive API is enabled and folder is shared.</p>
            ) : (
              <div className="space-y-2">
                {backups.google_drive_backups.map((backup, idx) => (
                  <div key={idx} className="flex items-center justify-between bg-[#0A0A0A] border border-white/5 rounded-sm p-3">
                    <div className="flex items-center gap-3">
                      <Cloud className="w-4 h-4 text-green-500" />
                      <div>
                        <p className="text-white text-sm font-medium">{backup.filename}</p>
                        <p className="text-gray-500 text-xs">
                          {formatDate(backup.created_at)} • {backup.size_mb} MB
                        </p>
                      </div>
                    </div>
                    {backup.web_link && (
                      <a
                        href={backup.web_link}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex items-center gap-2 px-3 py-1 text-green-400 hover:bg-green-500/20 rounded-sm transition-colors text-sm"
                      >
                        <Download className="w-4 h-4" />
                        Open in Drive
                      </a>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Reset Everything */}
        <div className="bg-red-500/10 border border-red-500/30 rounded-sm p-6">
          <div className="flex items-start gap-4">
            <div className="w-12 h-12 bg-red-500/20 rounded-sm flex items-center justify-center flex-shrink-0">
              <AlertCircle className="w-6 h-6 text-red-400" />
            </div>
            <div className="flex-1">
              <h3 className="text-red-400 font-bold text-lg mb-2">Reset All Test Data</h3>
              <p className="text-gray-400 text-sm mb-4">
                This will clear all proposals, cards, contacts, financial data, and activity logs at once. 
                Products, templates, and library sections will be preserved.
              </p>
              <button
                onClick={() => startConfirmationFlow('reset-all', 'Reset Everything')}
                disabled={!canPerformActions}
                className="flex items-center gap-2 px-6 py-2 bg-red-500 hover:bg-red-600 text-white font-bold rounded-sm transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <Trash2 className="w-4 h-4" />
                Reset Everything
              </button>
            </div>
          </div>
        </div>

        {/* PIN Verification Modal */}
        {showPinModal && (
          <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4">
            <div className="bg-[#0A0A0A] border border-yellow-500/30 rounded-sm w-full max-w-md">
              <div className="p-4 border-b border-yellow-500/30 bg-yellow-500/10">
                <h2 className="text-lg font-bold text-yellow-400 flex items-center gap-2">
                  <Key className="w-5 h-5" />
                  Enter Admin PIN
                </h2>
              </div>
              
              <div className="p-4 space-y-4">
                <p className="text-gray-400">
                  Enter your admin PIN to enable sandbox operations in this production environment.
                </p>
                
                <input
                  type="password"
                  value={pin}
                  onChange={(e) => setPin(e.target.value)}
                  placeholder="Enter PIN"
                  maxLength={8}
                  className="w-full bg-[#111] border border-white/20 rounded-sm text-white text-center text-2xl tracking-widest px-3 py-3 focus:border-yellow-500 outline-none"
                  onKeyDown={(e) => e.key === 'Enter' && verifyPin()}
                />
              </div>
              
              <div className="p-4 border-t border-white/10 flex gap-3">
                <button
                  onClick={() => { setShowPinModal(false); setPin(''); }}
                  className="flex-1 px-4 py-2 bg-gray-700 hover:bg-gray-600 text-white rounded-sm transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={verifyPin}
                  disabled={verifyingPin || !pin}
                  className="flex-1 px-4 py-2 bg-yellow-500 hover:bg-yellow-600 text-black font-bold rounded-sm transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                >
                  {verifyingPin ? (
                    <>
                      <RefreshCw className="w-4 h-4 animate-spin" />
                      Verifying...
                    </>
                  ) : (
                    <>
                      <Lock className="w-4 h-4" />
                      Verify PIN
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* 3-Step Confirmation Modal */}
        {confirmationFlow.active && (
          <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4">
            <div className="bg-[#0A0A0A] border border-red-500/30 rounded-sm w-full max-w-lg">
              {/* Header */}
              <div className="p-4 border-b border-red-500/30 bg-red-500/10">
                <h2 className="text-lg font-bold text-red-400 flex items-center gap-2">
                  <AlertTriangle className="w-5 h-5" />
                  {confirmationFlow.action} - Step {confirmationFlow.step} of 3
                </h2>
              </div>
              
              {/* Progress Indicator */}
              <div className="px-4 pt-4">
                <div className="flex items-center justify-between mb-4">
                  {[1, 2, 3].map((step) => (
                    <div key={step} className="flex items-center">
                      <div className={`w-8 h-8 rounded-full flex items-center justify-center font-bold ${
                        step < confirmationFlow.step 
                          ? 'bg-green-500 text-white' 
                          : step === confirmationFlow.step 
                            ? 'bg-red-500 text-white' 
                            : 'bg-gray-700 text-gray-400'
                      }`}>
                        {step < confirmationFlow.step ? <CheckCircle className="w-5 h-5" /> : step}
                      </div>
                      {step < 3 && (
                        <div className={`w-16 h-1 mx-2 ${
                          step < confirmationFlow.step ? 'bg-green-500' : 'bg-gray-700'
                        }`} />
                      )}
                    </div>
                  ))}
                </div>
              </div>
              
              {/* Content */}
              <div className="p-4 space-y-4">
                {confirmationFlow.step === 1 && (
                  <>
                    <h3 className="text-white font-bold">Step 1: Acknowledge the Impact</h3>
                    <p className="text-gray-400">
                      You are about to permanently delete data. This action:
                    </p>
                    <ul className="text-sm text-gray-400 space-y-2 ml-4">
                      <li className="flex items-center gap-2">
                        <XCircle className="w-4 h-4 text-red-400" />
                        Cannot be undone
                      </li>
                      <li className="flex items-center gap-2">
                        <XCircle className="w-4 h-4 text-red-400" />
                        Will permanently remove all {confirmationFlow.type === 'reset-all' ? 'test' : confirmationFlow.type} data
                      </li>
                      <li className="flex items-center gap-2">
                        <XCircle className="w-4 h-4 text-red-400" />
                        May affect related records and reports
                      </li>
                    </ul>
                    <p className="text-yellow-400 font-medium mt-4">
                      Do you understand and wish to proceed?
                    </p>
                  </>
                )}
                
                {confirmationFlow.step === 2 && (
                  <>
                    <h3 className="text-white font-bold">Step 2: Type Confirmation</h3>
                    <p className="text-gray-400">
                      To continue, type <span className="text-red-400 font-bold">{getConfirmationText(confirmationFlow.type)}</span> exactly:
                    </p>
                    <input
                      type="text"
                      value={confirmationFlow.typedConfirm}
                      onChange={(e) => setConfirmationFlow(prev => ({ ...prev, typedConfirm: e.target.value }))}
                      placeholder={getConfirmationText(confirmationFlow.type)}
                      className="w-full bg-[#111] border border-white/20 rounded-sm text-white px-3 py-2 focus:border-red-500 outline-none"
                    />
                  </>
                )}
                
                {confirmationFlow.step === 3 && (
                  <>
                    <h3 className="text-white font-bold">Step 3: Final Confirmation</h3>
                    <div className="bg-red-500/20 border border-red-500/50 rounded-sm p-4">
                      <p className="text-red-400 font-bold text-center">
                        ⚠️ FINAL WARNING ⚠️
                      </p>
                      <p className="text-gray-300 text-sm mt-2 text-center">
                        This is your last chance to cancel. Once you confirm, the data will be permanently deleted.
                      </p>
                    </div>
                    <p className="text-gray-400 mt-4">
                      Type <span className="text-red-400 font-bold">CONFIRM</span> to execute:
                    </p>
                    <input
                      type="text"
                      value={confirmationFlow.finalConfirm}
                      onChange={(e) => setConfirmationFlow(prev => ({ ...prev, finalConfirm: e.target.value }))}
                      placeholder="CONFIRM"
                      className="w-full bg-[#111] border border-white/20 rounded-sm text-white px-3 py-2 focus:border-red-500 outline-none"
                    />
                  </>
                )}
              </div>
              
              {/* Footer */}
              <div className="p-4 border-t border-white/10 flex gap-3">
                <button
                  onClick={cancelConfirmationFlow}
                  className="flex-1 px-4 py-2 bg-gray-700 hover:bg-gray-600 text-white rounded-sm transition-colors"
                >
                  Cancel
                </button>
                {confirmationFlow.step < 3 ? (
                  <button
                    onClick={nextStep}
                    className="flex-1 px-4 py-2 bg-red-500 hover:bg-red-600 text-white font-bold rounded-sm transition-colors flex items-center justify-center gap-2"
                  >
                    Continue
                    <ArrowRight className="w-4 h-4" />
                  </button>
                ) : (
                  <button
                    onClick={executeAction}
                    disabled={clearing[confirmationFlow.type] || confirmationFlow.finalConfirm !== 'CONFIRM'}
                    className="flex-1 px-4 py-2 bg-red-500 hover:bg-red-600 text-white font-bold rounded-sm transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                  >
                    {clearing[confirmationFlow.type] ? (
                      <>
                        <RefreshCw className="w-4 h-4 animate-spin" />
                        Deleting...
                      </>
                    ) : (
                      <>
                        <Trash2 className="w-4 h-4" />
                        Execute Delete
                      </>
                    )}
                  </button>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Empty Contacts Cleanup Modal */}
        {showEmptyContactsModal && (
          <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4">
            <div className="bg-[#0A0A0A] border border-cyan-500/30 rounded-sm w-full max-w-lg max-h-[80vh] flex flex-col">
              {/* Header */}
              <div className="p-4 border-b border-cyan-500/30 bg-cyan-500/10 flex-shrink-0">
                <h2 className="text-lg font-bold text-cyan-400 flex items-center gap-2">
                  <UserX className="w-5 h-5" />
                  Empty Field Clean - Preview
                </h2>
              </div>
              
              {/* Content */}
              <div className="p-4 space-y-4 overflow-y-auto flex-1">
                {emptyContactsPreview && (
                  <>
                    {/* Summary */}
                    <div className={`p-4 rounded-sm border ${
                      emptyContactsPreview.total_empty_contacts > 0 
                        ? 'bg-yellow-500/10 border-yellow-500/30' 
                        : 'bg-green-500/10 border-green-500/30'
                    }`}>
                      {emptyContactsPreview.total_empty_contacts > 0 ? (
                        <>
                          <p className="text-yellow-400 font-bold text-lg">
                            {emptyContactsPreview.total_empty_contacts} empty contact{emptyContactsPreview.total_empty_contacts !== 1 ? 's' : ''} found
                          </p>
                          <p className="text-gray-400 text-sm">
                            Across {emptyContactsPreview.cards_affected} card{emptyContactsPreview.cards_affected !== 1 ? 's' : ''}
                          </p>
                        </>
                      ) : (
                        <>
                          <p className="text-green-400 font-bold text-lg flex items-center gap-2">
                            <CheckCircle className="w-5 h-5" />
                            No empty contacts found
                          </p>
                          <p className="text-gray-400 text-sm">
                            Your contact data is clean!
                          </p>
                        </>
                      )}
                    </div>

                    {/* Details */}
                    {emptyContactsPreview.details && emptyContactsPreview.details.length > 0 && (
                      <div>
                        <h3 className="text-white font-medium mb-2">Affected Cards:</h3>
                        <div className="space-y-2 max-h-48 overflow-y-auto">
                          {emptyContactsPreview.details.map((card, idx) => (
                            <div key={idx} className="bg-[#111] border border-white/10 rounded-sm p-3 text-sm">
                              <div className="flex justify-between items-center">
                                <span className="text-white font-medium">{card.company_name}</span>
                                <span className="text-yellow-400">
                                  {card.empty_contacts} empty / {card.total_contacts} total
                                </span>
                              </div>
                            </div>
                          ))}
                        </div>
                        {emptyContactsPreview.cards_affected > 20 && (
                          <p className="text-gray-500 text-xs mt-2">
                            Showing first 20 of {emptyContactsPreview.cards_affected} cards
                          </p>
                        )}
                      </div>
                    )}

                    {/* What will happen */}
                    {emptyContactsPreview.total_empty_contacts > 0 && (
                      <div className="bg-[#111] border border-white/10 rounded-sm p-4">
                        <h3 className="text-white font-medium mb-2">What will be cleaned:</h3>
                        <ul className="text-gray-400 text-sm space-y-1">
                          <li className="flex items-center gap-2">
                            <CheckCircle className="w-4 h-4 text-green-400" />
                            Contacts with no first name, last name, name, OR email
                          </li>
                          <li className="flex items-center gap-2">
                            <Shield className="w-4 h-4 text-cyan-400" />
                            Contacts with ANY identifying info are preserved
                          </li>
                          <li className="flex items-center gap-2">
                            <Shield className="w-4 h-4 text-cyan-400" />
                            Cards themselves are NOT deleted
                          </li>
                        </ul>
                      </div>
                    )}
                  </>
                )}
              </div>
              
              {/* Footer */}
              <div className="p-4 border-t border-white/10 flex gap-3 flex-shrink-0">
                <button
                  onClick={() => {
                    setShowEmptyContactsModal(false);
                    setEmptyContactsPreview(null);
                  }}
                  className="flex-1 px-4 py-2 bg-gray-700 hover:bg-gray-600 text-white rounded-sm transition-colors"
                >
                  Cancel
                </button>
                {emptyContactsPreview && emptyContactsPreview.total_empty_contacts > 0 && (
                  <button
                    onClick={executeEmptyContactsCleanup}
                    disabled={cleaningContacts}
                    className="flex-1 px-4 py-2 bg-cyan-500 hover:bg-cyan-600 text-black font-bold rounded-sm transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                    data-testid="confirm-empty-field-clean-btn"
                  >
                    {cleaningContacts ? (
                      <>
                        <RefreshCw className="w-4 h-4 animate-spin" />
                        Cleaning...
                      </>
                    ) : (
                      <>
                        <Trash2 className="w-4 h-4" />
                        Clean {emptyContactsPreview.total_empty_contacts} Empty Contacts
                      </>
                    )}
                  </button>
                )}
              </div>
            </div>
          </div>
        )}
      </div>
    </AppLayout>
  );
};

// Data Card Component
const DataCard = ({ icon: Icon, iconBg, iconColor, title, count, description, onClear, clearing, disabled, borderColor = 'border-white/10' }) => (
  <div className={`bg-[#111] border ${borderColor} rounded-sm p-4`}>
    <div className="flex items-center justify-between">
      <div className="flex items-center gap-3">
        <div className={`w-10 h-10 ${iconBg} rounded-sm flex items-center justify-center`}>
          <Icon className={`w-5 h-5 ${iconColor}`} />
        </div>
        <div>
          <h3 className="text-white font-medium">{title}</h3>
          <p className="text-gray-500 text-sm">{typeof count === 'number' ? `${count} items` : count}</p>
        </div>
      </div>
      <button
        onClick={onClear}
        disabled={clearing || disabled}
        className="flex items-center gap-2 px-4 py-2 bg-red-500/20 hover:bg-red-500/30 text-red-400 rounded-sm transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
      >
        {clearing ? (
          <RefreshCw className="w-4 h-4 animate-spin" />
        ) : (
          <Trash2 className="w-4 h-4" />
        )}
        Clear
      </button>
    </div>
    <p className="text-gray-500 text-xs mt-2 ml-13">
      {description}
    </p>
  </div>
);

export default AdminSandbox;
